import Image from "next/image";
import Breadcrumb from "@/components/Breadcrumb";
import { Link } from "@/i18n/routing";
import { Lightbulb, Star, Users, Search, BarChart2, Compass } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

const breadcrumbItems = [
  { label: "Home", href: "/" },
  { label: "About" }
];

const values = [
  {
    icon: <Lightbulb className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Innovation",
    body: "We use modern technology, including AI for business automation, to solve business challenges in smarter and more efficient ways."
  },
  {
    icon: <Star className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Excellence",
    body: "As a software development company in Switzerland, we focus on quality, performance, and long-term reliability in every solution we deliver."
  },
  {
    icon: <Users className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Customer Focus",
    body: "Your business goals guide everything we build from websites and SaaS platforms to AI automation services and digital systems."
  },
  {
    icon: <Search className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Transparency",
    body: "We believe in clear communication, honest advice, and practical solutions that deliver real value."
  },
  {
    icon: <BarChart2 className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Growth",
    body: "Our digital systems are designed to increase revenue, improve visibility, and help businesses scale across Switzerland and Europe."
  },
  {
    icon: <Compass className="w-10 h-10 mb-6 text-accent-red" />,
    title: "Ownership",
    body: "We work as a committed partner, taking responsibility for delivering outcomes that matter — not just completing tasks."
  },
];

const processSteps = [
  {
    step: "01",
    title: "Strategy",
    body: "We start by understanding your business, goals, and challenges. Whether you need AI consulting services in Europe, a website, or a SaaS product, we identify the best approach for your success."
  },
  {
    step: "02",
    title: "Design",
    body: "We create modern, user-focused designs that improve customer experience and support conversions, especially for businesses that rely on digital growth and direct bookings."
  },
  {
    step: "03",
    title: "Development",
    body: "We build reliable digital systems including websites, AI automation services, SaaS platforms, and custom applications designed for performance and scalability."
  },
  {
    step: "04",
    title: "Growth",
    body: "We continuously optimize your systems using data, insights, and improvements to support long-term business growth and efficiency."
  },
];

export default function About({ params }) {
  const locale = normalizeLocale(params?.locale);
  const canonical = sitePageUrl(locale, "about");
  const homeUrl = sitePageUrl(locale);
  const orgId = `${SITE_URL}/#organization`;

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: "Highlandgroup",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
        description:
          "Highlandgroup is a software development company in Switzerland delivering AI, automation, SaaS, and digital solutions for European businesses.",
        areaServed: ["Switzerland", "Europe"],
      },
      {
        "@type": "AboutPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: "About Highlandgroup | Software Company Switzerland",
        description:
          "Learn about Highlandgroup, a software development company in Switzerland delivering AI, automation, SaaS, and digital solutions for European businesses.",
        inLanguage: locale,
        about: { "@id": orgId },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "About", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>About Highlandgroup | Software Company Switzerland</title>
        <meta
          name="description"
          content="Learn about Highlandgroup, a software development company in Switzerland delivering AI, automation, SaaS, and digital solutions for European businesses."
        />
        <meta
          name="keywords"
          content="software development company Switzerland, AI solutions company, software team Switzerland, book AI consultation Switzerland"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="About Highlandgroup" />
        <meta
          property="og:description"
          content="Meet the Swiss software and AI team behind scalable digital solutions for Europe."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Highlandgroup" />
        <meta
          name="twitter:description"
          content="Meet the Swiss software and AI team behind scalable digital solutions for Europe."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-bg-soft text-charcoal selection:bg-accent-red/30">

        {/* Hero Section */}
        <Breadcrumb
          items={breadcrumbItems}
          title="We Build Digital Growth Systems for Swiss Businesses"
          subtitle="Highland Group is an AI for business automation and software development company in Switzerland helping businesses grow through smart digital systems. We combine AI automation services, website development, SaaS products, and hotel digital solutions to create systems that increase revenue, improve efficiency, and support long-term growth."
          description="From small businesses to international clients across Europe, we build practical solutions that deliver measurable results, not just technology."
        />

       

        {/* Who We Are */}
        <section className="py-16 bg-bg-soft border-y border-border-light">
          <div className="container mx-auto px-8">
            <div className="grid md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-7 space-y-6">
                <h2 className="text-3xl font-semibold text-charcoal">Who We Are</h2>
                <p className="text-lg text-charcoal leading-relaxed max-w-2xl">
                  Highland Group is a modern software development agency in Switzerland working with both domestic and international clients across Europe. We specialize in building digital systems that solve real business problems and create real growth opportunities.
                </p>
                <p className="text-lg text-charcoal leading-relaxed max-w-2xl">
                  As an experienced AI automation company in Switzerland, we help businesses automate workflows, improve customer experience, and reduce manual work using intelligent systems.
                </p>
                <div className="text-base text-charcoal leading-relaxed">
                  <p className="font-semibold mb-3">Our work includes:</p>
                  <ul className="space-y-2 list-disc pl-5 marker:text-accent-red">
                    <li>AI automation services for businesses in Switzerland</li>
                    <li>Custom software and SaaS product development</li>
                    <li>High-performance websites as a trusted web development company in Switzerland</li>
                    <li>Scalable hotel digital solutions for hospitality businesses</li>
                  </ul>
                </div>
                <p className="text-lg text-charcoal leading-relaxed max-w-2xl">
                  We focus on building systems that increase direct bookings, generate qualified leads, and improve operational efficiency across industries.
                </p>
                <div className="pt-2 border-t border-border-light max-w-2xl">
                  <p className="text-xl font-semibold text-charcoal">Highland Group</p>
                  <p className="text-muted">We are not just developers, we are your digital growth partner.</p>
                </div>
              </div>

              <div className="md:col-span-5 h-[400px] relative rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src="https://images.pexels.com/photos/1181355/pexels-photo-1181355.jpeg"
                  alt="Highland Group Office"
                  fill
                  className="object-cover grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 bg-bg-soft">
          <div className="container mx-auto px-8">
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-4 text-charcoal">Our Core Values</h2>
              <div className="h-1 w-20 bg-accent-red" />
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="p-8 rounded-2xl bg-bg-soft border border-border-light hover:border-accent-red/30 transition-all group">
                  <span className="text-4xl mb-6 block">{v.icon}</span>
                  <h3 className="text-xl font-bold mb-3 text-charcoal">{v.title}</h3>
                  <p className="text-muted leading-relaxed text-sm">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-12 bg-bg-soft border-t border-border-light">
          <div className="container mx-auto px-6 md:px-8 lg:px-12">
            <h2 className="text-3xl font-bold mb-16 text-charcoal">How We Work</h2>

            <div className="relative max-w-5xl mx-auto">
              <div className="absolute left-7 md:left-1/2 top-0 bottom-0 w-0.5 bg-border-light hidden md:block transform -translate-x-1/2" />

              {processSteps.map((step, idx) => {
                const isEven = idx % 2 === 0;

                return (
                  <div
                    key={idx}
                    className={`relative mb-12 md:mb-16 last:mb-0 md:flex md:items-start ${isEven ? 'md:justify-start' : 'md:justify-end'}`}
                  >
                    {/* Circle */}
                    <div className={`flex items-center justify-center w-14 h-14 rounded-full bg-accent-red/10 border-2 border-accent-red text-accent-red font-black text-2xl z-10 shrink-0 ${isEven ? 'md:mr-8' : 'md:ml-8 md:order-2'}`}>
                      {step.step}
                    </div>

                    {/* Card */}
                    <div className={`bg-bg-main border border-border-light rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 flex-1 max-w-lg ${isEven ? '' : 'md:ml-auto'}`}>
                      <h3 className="text-xl font-bold text-accent-red mb-3">
                        {step.title}
                      </h3>
                      <p className="text-muted leading-relaxed">
                        {step.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="py-16 bg-bg-soft border-t border-border-light">
          <div className="container mx-auto px-8 text-center max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal">
              Let&apos;s Build Your Digital Growth System
            </h2>
            <p className="mt-4 text-lg text-muted leading-relaxed">
              We build practical digital solutions that help businesses attract more customers, improve efficiency, and grow with confidence.
            </p>
            <Link
              href="/contact"
              className="mt-6 inline-block rounded-full bg-accent-red px-8 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Book your free consultation today
            </Link>
          </div>
        </section>

      </main>
    </>
  );
}

// md:mr-auto md:text-right